import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api, setAccessToken, setUnauthorizedHandler } from "@/lib/api";
import type { AdminPermission, FeatureAccessMap, LoginResponse, UserMe } from "@/types/auth";

interface AuthState {
  user: UserMe | null;
  features: FeatureAccessMap;
  status: "idle" | "loading" | "authenticated" | "unauthenticated";
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  hasPermission: (perm: AdminPermission) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      features: {},
      status: "idle",
      error: null,

      async login(email, password) {
        set({ status: "loading", error: null });
        try {
          const { data } = await api.post<LoginResponse>("/auth/web/login", { email, password });
          setAccessToken(data.access_token);
          set({ user: data.user, status: "authenticated", error: null });
          await loadFeatures(set);
        } catch (err: unknown) {
          const message = extractError(err) ?? "Login failed";
          set({ status: "unauthenticated", error: message, user: null });
          throw err;
        }
      },

      async logout() {
        try {
          await api.post("/auth/web/logout");
        } catch {
          /* ignore */
        }
        setAccessToken(null);
        set({ user: null, features: {}, status: "unauthenticated", error: null });
      },

      async bootstrap() {
        // Try refresh first (the access token isn't persisted across reloads).
        set({ status: "loading" });
        try {
          const { data } = await api.post<LoginResponse>("/auth/web/refresh");
          setAccessToken(data.access_token);
          set({ user: data.user, status: "authenticated", error: null });
          await loadFeatures(set);
        } catch {
          setAccessToken(null);
          set({ user: null, features: {}, status: "unauthenticated" });
        }
      },

      hasPermission(perm) {
        const u = get().user;
        if (!u) return false;
        if (u.is_superadmin) return true;
        return u.admin_permissions.includes(perm);
      },
    }),
    {
      name: "inout-auth",
      partialize: (s) => ({ user: s.user }), // only cache identity, not tokens
    }
  )
);

async function loadFeatures(set: (partial: Partial<AuthState>) => void) {
  try {
    const { data } = await api.get<FeatureAccessMap>("/api/web/features");
    set({ features: data });
  } catch {
    set({ features: {} });
  }
}

function extractError(err: unknown): string | null {
  if (err && typeof err === "object" && "response" in err) {
    const r = (err as { response?: { data?: { detail?: { error?: { message?: string } } | string } } }).response;
    const detail = r?.data?.detail;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object" && "error" in detail && detail.error?.message) {
      return detail.error.message;
    }
  }
  return null;
}

// Wire global 401 → logout (set after store creation to avoid circular import)
setUnauthorizedHandler(() => {
  const { logout, status } = useAuthStore.getState();
  if (status === "authenticated") void logout();
});
